import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Rocket, LogOut, Loader2, Inbox, Check, X, Clock, CreditCard, XCircle, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { adminCheck, adminListRequests, adminAcceptRequest, adminDeclineRequest, adminLogout } from "../lib/api";
import { AdminNav } from "./AdminContinueBuilding";

const FILTERS = [
  { key: "in_review", label: "In Review", icon: Clock },
  { key: "accepted", label: "Approved", icon: CreditCard },
  { key: "declined", label: "Declined", icon: XCircle },
];

export default function AdminRequests() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("in_review");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    adminCheck()
      .then((r) => { if (!r.admin) { setAuthorized(false); navigate("/"); return; } setAuthorized(true); })
      .catch(() => { setAuthorized(false); navigate("/"); });
  }, [navigate]);

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const data = await adminListRequests({ status, limit: 100 });
      setItems(data.items);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (authorized) load(filter); }, [authorized, filter, load]);

  const accept = async (id) => {
    setBusy(id);
    try { await adminAcceptRequest(id); toast.success("Request approved — customer can now pay"); await load(filter); }
    catch { toast.error("Failed to approve"); }
    finally { setBusy(null); }
  };
  const decline = async (id) => {
    setBusy(id);
    try { await adminDeclineRequest(id); toast.success("Request declined"); await load(filter); }
    catch { toast.error("Failed to decline"); }
    finally { setBusy(null); }
  };

  if (authorized === null) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0B0B0B]"><Loader2 className="animate-spin text-[#D4AF37]" size={30} /></div>;
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white" data-testid="admin-requests">
      <header className="border-b border-white/10 bg-[#0B0B0B]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center"><Rocket className="text-[#D4AF37]" size={18} /></div>
              <div><p className="font-display font-bold leading-tight">Command Center</p><p className="text-xs text-[#71717A]">Preview Requests</p></div>
            </div>
            <button onClick={async () => { await adminLogout(); navigate("/"); }} className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white transition-colors" data-testid="admin-logout-btn">
              <LogOut size={16} /> Sign out
            </button>
          </div>
          <AdminNav active="requests" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex gap-2 mb-6">
          {FILTERS.map((f) => {
            const Icon = f.icon;
            return (
              <button key={f.key} onClick={() => setFilter(f.key)} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${filter === f.key ? "bg-[#D4AF37] text-[#0F0F0F]" : "bg-white/5 text-[#A1A1AA] hover:bg-white/10"}`} data-testid={`req-filter-${f.key}`}>
                <Icon size={14} /> {f.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#D4AF37]" size={26} /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 bg-white/[0.03] border border-white/10 rounded-2xl" data-testid="admin-requests-empty">
            <Inbox className="mx-auto text-[#3f3f46] mb-3" size={34} />
            <p className="font-display font-semibold">No {FILTERS.find((f) => f.key === filter)?.label.toLowerCase()} requests</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-requests-list">
            {items.map((r) => (
              <RequestRow key={r.id} req={r} busy={busy === r.id} onAccept={() => accept(r.id)} onDecline={() => decline(r.id)} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

const RequestRow = ({ req, busy, onAccept, onDecline }) => {
  const [open, setOpen] = useState(false);
  const detail = (label, value) => value ? (
    <div><dt className="text-[#71717A] text-xs">{label}</dt><dd className="text-white/90 whitespace-pre-wrap text-sm">{value}</dd></div>
  ) : null;

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5" data-testid={`admin-request-${req.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-bold text-lg">{req.project_name}</h3>
            {req.app_category && <span className="text-xs bg-white/10 rounded-full px-2.5 py-0.5">{req.app_category}</span>}
          </div>
          <p className="text-sm text-[#A1A1AA] mt-1">{req.customer_email} · {(req.created_at || "").slice(0, 10)}</p>
          <p className="text-sm text-white/80 mt-2 line-clamp-2">{req.idea_description}</p>
          <button onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 text-xs text-[#D4AF37] mt-2 hover:underline" data-testid={`req-details-toggle-${req.id}`}>
            {open ? <>Hide details <ChevronUp size={13} /></> : <>View details <ChevronDown size={13} /></>}
          </button>
        </div>
        {req.status === "in_review" && (
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={onAccept} disabled={busy} className="inline-flex items-center gap-1.5 bg-emerald-500 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50" data-testid={`req-accept-${req.id}`}>
              {busy ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Accept
            </button>
            <button onClick={onDecline} disabled={busy} className="inline-flex items-center gap-1.5 border border-white/15 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-white/5 transition-colors disabled:opacity-50" data-testid={`req-decline-${req.id}`}>
              <X size={14} /> Decline
            </button>
          </div>
        )}
        {req.status === "accepted" && <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 text-emerald-400 px-3 py-1 text-xs font-semibold shrink-0"><CreditCard size={12} /> Awaiting payment</span>}
        {req.status === "declined" && <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/15 text-red-400 px-3 py-1 text-xs font-semibold shrink-0"><XCircle size={12} /> Declined</span>}
      </div>

      {open && (
        <dl className="grid sm:grid-cols-2 gap-4 mt-5 pt-5 border-t border-white/10">
          {detail("Problem", req.problem_statement)}
          {detail("Target users", req.target_users)}
          {detail("Requested features", req.requested_features)}
          {detail("Design style", req.design_style)}
          {detail("Preferred colors", req.preferred_colors)}
          {detail("Reference links", req.reference_links)}
          {detail("Additional notes", req.additional_notes)}
          {detail("Business name", req.business_name)}
          {detail("Phone", req.phone)}
        </dl>
      )}
    </div>
  );
};
